import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

// Extend dayjs with the necessary plugins
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export function calculateDateWithDays(
    days: number,
    startDate?: Date, // Optional start date
    includeWeekends = true,
    includeHolidays = true
): Date {
    let currentDate = startDate ? dayjs(startDate) : dayjs(); // Start date
    let remainingDays = days;

    if (remainingDays < 0) return currentDate.toDate();

    // Initialize holidays for the current year
    let holidays: dayjs.Dayjs[] = !includeHolidays
        ? getColombianHolidays(currentDate.year())
        : [];

    // Loop until the remaining days are exhausted
    while (remainingDays >= 0) {
        // If we enter a new year, load the holidays for the new year
        if (currentDate.year() !== holidays[holidays.length - 1]?.year()) {
            holidays = holidays.concat(
                getColombianHolidays(currentDate.year())
            );
        }

        // Check if the current day is a weekend
        const isWeekend = currentDate.day() === 0 || currentDate.day() === 6; // Sunday (0) or Saturday (6)

        // Check if the current day is a holiday
        const isHoliday = holidays.some((holiday) =>
            holiday.isSame(currentDate, 'day')
        );

        currentDate = currentDate.add(1, 'day'); // Always increment the day

        if (isWeekend && (!includeWeekends || remainingDays === 0)) continue;
        if (isHoliday && (!includeHolidays || remainingDays === 0)) continue;

        if (remainingDays === 0) currentDate = currentDate.subtract(1, 'day');

        remainingDays--;
    }

    return currentDate.toDate();
}

// Function to calculate Colombian holidays
function getColombianHolidays(year: number): dayjs.Dayjs[] {
    const holidays: dayjs.Dayjs[] = [];

    // Fixed holidays
    holidays.push(dayjs(`${year}-01-01`)); // New Year's Day
    holidays.push(dayjs(`${year}-05-01`)); // Labor Day
    holidays.push(dayjs(`${year}-07-20`)); // Independence Day
    holidays.push(dayjs(`${year}-08-07`)); // Battle of Boyacá
    holidays.push(dayjs(`${year}-12-08`)); // Immaculate Conception
    holidays.push(dayjs(`${year}-12-25`)); // Christmas

    // Movable holidays
    holidays.push(getNextMonday(dayjs(`${year}-01-06`))); // Epiphany
    holidays.push(getNextMonday(dayjs(`${year}-03-19`))); // Saint Joseph's Day
    holidays.push(getNextMonday(dayjs(`${year}-06-29`))); // Saint Peter and Saint Paul
    holidays.push(getNextMonday(dayjs(`${year}-08-15`))); // Assumption of Mary
    holidays.push(getNextMonday(dayjs(`${year}-10-12`))); // Columbus Day
    holidays.push(getNextMonday(dayjs(`${year}-11-01`))); // All Saints' Day
    holidays.push(getNextMonday(dayjs(`${year}-11-11`))); // Cartagena's Independence

    // Religious holidays
    const easterSunday = getEasterSunday(year);
    holidays.push(easterSunday); // Easter Sunday
    holidays.push(easterSunday.subtract(3, 'day')); // Maundy Thursday
    holidays.push(easterSunday.subtract(2, 'day')); // Good Friday
    holidays.push(easterSunday.add(43, 'day')); // Ascension of Jesus
    holidays.push(easterSunday.add(64, 'day')); // Corpus Christi
    holidays.push(easterSunday.add(71, 'day')); // Sacred Heart

    return holidays.sort((a, b) => a.valueOf() - b.valueOf());
}

// Function to get the next Monday
function getNextMonday(date: dayjs.Dayjs): dayjs.Dayjs {
    const dayOfWeek = date.day();
    const daysUntilMonday = (8 - dayOfWeek) % 7;
    return date.add(daysUntilMonday, 'day');
}

// Calculation of Easter Sunday date (Gauss's algorithm)
function getEasterSunday(year: number): dayjs.Dayjs {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31) - 1;
    const day = ((h + l - 7 * m + 114) % 31) + 1;

    return dayjs(new Date(year, month, day));
}

export function getValidDateToValidationSchema(value: unknown): Date | null {
    if (!value) return null;

    const dateValue =
        Array.isArray(value) && value.length > 0 ? value[0] : value;

    return dayjs(dateValue).isValid() ? dayjs(dateValue).toDate() : null;
}

export function calculateMinDate(...dates: unknown[]) {
    const newDates = dates.filter((d) => !!d);
    if (newDates.length === 0) return dayjs();

    return newDates
        .map(getValidDateToValidationSchema)
        .filter(Boolean)
        .reduce((max, current) =>
            dayjs(current).isAfter(max) ? current : max
        );
}

export function calculateDateDifference(
    startDate: Date,
    endDate: Date,
    isCalendarDays: boolean
): number {
    const endDateObj = new Date(endDate);
    if (isCalendarDays) {
        const timeDiff = endDateObj.getTime() - startDate.getTime();
        return Math.ceil(timeDiff / (1000 * 3600 * 24));
    }
    return calculateBusinessDays(startDate, endDateObj);
}

function calculateBusinessDays(startDate: Date, endDate: Date) {
    let count = 0;
    const curDate = new Date(startDate.getTime());
    while (curDate <= endDate) {
        const dayOfWeek = curDate.getDay();
        if (dayOfWeek !== 0 && dayOfWeek !== 6) count++;
        curDate.setDate(curDate.getDate() + 1);
    }
    return count;
}
